import { Box } from '@mui/material'
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'
import CountertopsIcon from '@mui/icons-material/Countertops'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FlagIcon from '@mui/icons-material/Flag'
import EngineeringIcon from '@mui/icons-material/Engineering'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import InventoryIcon from '@mui/icons-material/Inventory2'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ScienceIcon from '@mui/icons-material/Science'
import SyncIcon from '@mui/icons-material/Sync'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import HubIcon from '@mui/icons-material/Hub'
import StorageIcon from '@mui/icons-material/Storage'
import { FONT_MONO, FONT_SANS, TOKENS } from './SchedulerDesign'
import { useScheduleProductionRequirements } from './Scheduler.data'
import {
  SchedulerPackagingRoutesRowType,
  WorkstationDownTimeRowType,
  SchedulerCleningMappingRowType,
  SynchronizedLinesRowType,
} from './Scheduler.types'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { ResourceGantt } from './ScheduleGantt.view'

/**
 * Bottom detail panel shown when a node (kitchen / line / CIP) is selected
 * on the Factory Layout. Spec: `FactoryDetail.jsx` → `FactoryBottomDetail`.
 *
 * Three sub-tabs: Overview · Input · Schedule.
 * Overview: downtime context card + source tables strip.
 * Input: expandable product/cleaning/sync accordions.
 * Schedule: single-resource Gantt from schedule output.
 */

const ACCENT = {
  kitchen: TOKENS.brandTeal,
  line: TOKENS.brandBlue,
  cip: TOKENS.ganttClean,
}
const TYPE_ICON = {
  kitchen: CountertopsIcon,
  line: PrecisionManufacturingIcon,
  cip: CleaningServicesIcon,
}
const TYPE_LABEL = { kitchen: 'Kitchen', line: 'Line', cip: 'CIP' }

const T = { fontFamily: FONT_SANS }
const MONO_SX = { fontFamily: FONT_MONO, fontVariantNumeric: 'tabular-nums' }

const Row = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {children}
  </Box>
)

/** Accordion section used in Input tab */
const FDAccordion = ({
  icon,
  title,
  count,
  defaultOpen = false,
  onOpenTable,
  children,
}: {
  icon: React.ReactNode
  title: string
  count?: number
  defaultOpen?: boolean
  onOpenTable?: () => void
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '2px',
        mb: 1,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          p: '8px 10px',
          background: TOKENS.panelHead,
          cursor: 'pointer',
          '&:hover': { background: TOKENS.line100 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            color: TOKENS.brandTeal,
            '& svg': { fontSize: 15 },
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            flex: 1,
            fontSize: 12,
            fontWeight: 700,
            color: TOKENS.ink700,
            ...T,
          }}
        >
          {title}
        </Box>
        {onOpenTable && (
          <Box
            component="span"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onOpenTable()
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.375,
              fontSize: 10,
              fontWeight: 600,
              color: TOKENS.blue600,
              cursor: 'pointer',
              px: '6px',
              py: '1px',
              borderRadius: '2px',
              border: `1px solid ${TOKENS.line200}`,
            }}
          >
            Open table <NorthEastIcon sx={{ fontSize: 11 }} />
          </Box>
        )}
        {count != null && (
          <Box
            component="span"
            sx={{
              ...MONO_SX,
              fontSize: 10.5,
              fontWeight: 700,
              background: TOKENS.line200,
              color: TOKENS.ink500,
              borderRadius: '2px',
              px: '6px',
              py: '1px',
              minWidth: 18,
              textAlign: 'center',
            }}
          >
            {count}
          </Box>
        )}
        <ExpandMoreIcon
          sx={{
            fontSize: 17,
            color: TOKENS.ink400,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </Box>
      {open && <Box sx={{ p: '10px' }}>{children}</Box>}
    </Box>
  )
}

const SubPaper = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      border: `1px solid ${TOKENS.line200}`,
      borderRadius: '2px',
      p: '8px 10px',
      mb: 0.75,
      background: '#fff',
    }}
  >
    {children}
  </Box>
)

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: '3px',
    }}
  >
    <Box component="span" sx={{ fontSize: 11, color: TOKENS.ink400, ...T }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{ ...MONO_SX, fontSize: 11.5, fontWeight: 600, color: TOKENS.ink700 }}
    >
      {value}
    </Box>
  </Box>
)

const SOURCE_TABLES = [
  {
    label: 'Production Requirement',
    icon: <AssignmentIcon sx={{ fontSize: 13 }} />,
  },
  { label: 'Product Master Data', icon: <ScienceIcon sx={{ fontSize: 13 }} /> },
  { label: 'Packaging Routes', icon: <HubIcon sx={{ fontSize: 13 }} /> },
  {
    label: 'Cleaning Mapping',
    icon: <CleaningServicesIcon sx={{ fontSize: 13 }} />,
  },
  {
    label: 'Workstation Downtime',
    icon: <EngineeringIcon sx={{ fontSize: 13 }} />,
  },
]
const SourceTablesStrip = ({
  onOpenTable,
}: {
  onOpenTable?: (t: string) => void
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      flexWrap: 'wrap',
      mb: 1.25,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: TOKENS.ink300,
      }}
    >
      <StorageIcon sx={{ fontSize: 13 }} /> Source data
    </Box>
    {SOURCE_TABLES.map(({ label, icon }) => (
      <Box
        key={label}
        component="button"
        onClick={() => onOpenTable?.(label)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          border: `1px solid ${TOKENS.line200}`,
          background: '#fff',
          borderRadius: '2px',
          px: '8px',
          py: '3px',
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 600,
          color: TOKENS.ink600,
          ...T,
          '& svg': { color: TOKENS.brandTeal },
        }}
      >
        {icon}
        {label}
        <NorthEastIcon sx={{ fontSize: 11, color: TOKENS.ink300 }} />
      </Box>
    ))}
  </Box>
)

/** Compact downtime context card */
const DowntimeCard = ({ downtimeStr }: { downtimeStr: string | null }) => {
  const windows = downtimeStr
    ? [...downtimeStr.matchAll(/\((\d+)\s*,\s*(\d+)\)/g)].map(
        (m) => [Number(m[1]), Number(m[2])] as [number, number]
      )
    : []
  const totalH = windows.reduce((s, w) => s + (w[1] - w[0]), 0)
  const accent = windows.length ? TOKENS.ganttDowntime : TOKENS.line300
  return (
    <Box
      sx={{
        flex: '1 1 230px',
        minWidth: 220,
        border: `1px solid ${TOKENS.line200}`,
        borderTop: `2px solid ${accent}`,
        borderRadius: '2px',
        background: '#fff',
        p: '9px 11px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.875 }}>
        <EngineeringIcon sx={{ fontSize: 14, color: accent }} />
        <Box
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: TOKENS.ink400,
            flex: 1,
          }}
        >
          Downtime
        </Box>
        <Box
          sx={{
            ...MONO_SX,
            fontSize: 10.5,
            fontWeight: 700,
            color: windows.length ? accent : TOKENS.ink300,
          }}
        >
          {totalH}h
        </Box>
      </Box>
      {/* mini strip */}
      <Box
        sx={{
          position: 'relative',
          height: 16,
          background: TOKENS.ganttIdle,
          borderRadius: '2px',
          border: `1px solid ${TOKENS.line200}`,
          overflow: 'hidden',
          mb: 0.75,
        }}
      >
        {[1, 2, 3, 4, 5, 6].map((d) => (
          <Box
            key={d}
            sx={{
              position: 'absolute',
              left: `${((d * 24) / 168) * 100}%`,
              top: 0,
              bottom: 0,
              width: '1px',
              background: 'rgba(14,27,34,0.08)',
            }}
          />
        ))}
        {windows.map((w, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${(w[0] / 168) * 100}%`,
              width: `${((w[1] - w[0]) / 168) * 100}%`,
              top: '2px',
              bottom: '2px',
              background: TOKENS.ganttDowntime,
              borderRadius: '1px',
            }}
          />
        ))}
      </Box>
      {windows.length ? (
        windows.map((w, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.625,
              fontSize: 11,
              color: TOKENS.ink700,
              mb: 0.25,
            }}
          >
            <Box
              component="span"
              sx={MONO_SX}
            >{`${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][Math.floor(w[0] / 24) % 7]} ${String(w[0] % 24).padStart(2, '0')}:00`}</Box>
            <Box component="span" sx={{ fontSize: 12, color: TOKENS.ink300 }}>
              →
            </Box>
            <Box
              component="span"
              sx={MONO_SX}
            >{`${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][Math.floor(w[1] / 24) % 7]} ${String(w[1] % 24).padStart(2, '0')}:00`}</Box>
            <Box component="span" sx={{ color: TOKENS.ink400 }}>
              · {w[1] - w[0]}h
            </Box>
          </Box>
        ))
      ) : (
        <Box sx={{ fontSize: 11, color: TOKENS.ink400 }}>
          No downtime scheduled this week.
        </Box>
      )}
    </Box>
  )
}

interface FactoryBottomDetailProps {
  /** Name of the selected node, e.g. "Kitchen A" or "Line 1" */
  name: string
  type: 'kitchen' | 'line' | 'cip'
  onClear: () => void
  /** Packaging routes — used to derive products on this kitchen/line. */
  packagingRoutes: SchedulerPackagingRoutesRowType[]
  downtimeData: WorkstationDownTimeRowType[]
  cleaningData: SchedulerCleningMappingRowType[]
  syncData: SynchronizedLinesRowType[]
  /** Schedule output rows for this resource. */
  scheduleRows: InputRow[]
  hasRun: boolean
  selectedWeek: number
  onOpenTable?: (table: string) => void
}

/**
 * Bottom detail panel — rebuilt from `FactoryDetail.jsx` FactoryBottomDetail.
 * Three tabs: Overview · Input · Schedule.
 */
export const FactoryBottomDetail = memo(
  ({
    name,
    type,
    onClear,
    packagingRoutes,
    downtimeData,
    cleaningData,
    syncData,
    scheduleRows,
    hasRun,
    selectedWeek,
    onOpenTable,
  }: FactoryBottomDetailProps) => {
    const accent = ACCENT[type]
    const TypeIcon = TYPE_ICON[type]
    const typeLabel = TYPE_LABEL[type]
    const [tab, setTab] = useState<'overview' | 'input' | 'schedule'>(
      'overview'
    )
    const [search, setSearch] = useState('')

    // Derive downtime string for this resource
    const downtimeStr = useMemo(() => {
      const row = downtimeData.find((r) => r.resource === name)
      return row?.downTime ?? null
    }, [downtimeData, name])

    // Derive products for this resource from packaging routes
    const products = useMemo(() => {
      if (type === 'kitchen') {
        const codes = new Set<string>()
        packagingRoutes
          .filter((r) => r.kitchen === name)
          .forEach((r) => codes.add(r.productCode))
        return [...codes]
      }
      if (type === 'line') {
        const codes = new Set<string>()
        packagingRoutes
          .filter((r) =>
            [
              r.packingLine1,
              r.packingLine2,
              r.packingLine3,
              r.packingLine4,
            ].includes(name)
          )
          .forEach((r) => codes.add(r.productCode))
        return [...codes]
      }
      return []
    }, [packagingRoutes, name, type])

    const cleaningPrograms = useMemo(
      () => cleaningData.filter((r) => r.asset === name || r.cip === name),
      [cleaningData, name]
    )

    const syncGroups = useMemo(
      () =>
        syncData.filter((r) =>
          r.lines
            ?.split(',')
            .map((l) => l.trim())
            .includes(name)
        ),
      [syncData, name]
    )

    const resourceSchedule = useMemo(
      () =>
        scheduleRows.filter(
          (r) =>
            (type === 'kitchen' && r.kitchen === name) ||
            (type === 'line' && r.line === name)
        ),
      [scheduleRows, name, type]
    )
    const hasProd = hasRun && resourceSchedule.length > 0

    const tabDefs = [
      { id: 'overview', label: 'Overview' },
      { id: 'input', label: 'Input' },
      ...(type !== 'cip' ? [{ id: 'schedule', label: 'Schedule' }] : []),
    ] as const

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          background: TOKENS.surface0,
          ...T,
        }}
      >
        {/* ── Header bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: '14px',
            py: '7px',
            borderBottom: `2px solid ${accent}`,
            flexShrink: 0,
          }}
        >
          <TypeIcon sx={{ fontSize: 15, color: accent }} />
          <Box
            component="span"
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: TOKENS.ink400,
            }}
          >
            {typeLabel}
          </Box>
          <Box
            component="span"
            sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink900 }}
          >
            {name}
          </Box>

          {/* tab switcher */}
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${TOKENS.line300}`,
              borderRadius: '2px',
              overflow: 'hidden',
              ml: 0.5,
            }}
          >
            {tabDefs.map(({ id, label }) => (
              <Box
                key={id}
                component="button"
                onClick={() => setTab(id as typeof tab)}
                sx={{
                  border: 'none',
                  px: '12px',
                  height: 24,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...T,
                  background: tab === id ? TOKENS.brandTeal : '#fff',
                  color: tab === id ? '#fff' : TOKENS.ink500,
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          <Box sx={{ flex: 1 }} />
          <Box
            component="span"
            sx={{ fontSize: 10.5, fontWeight: 600, color: TOKENS.ink400 }}
          >
            Week {selectedWeek}
          </Box>
          {/* search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.625,
              border: `1px solid ${TOKENS.line300}`,
              borderRadius: '2px',
              px: '7px',
              height: 26,
              width: 180,
            }}
          >
            <SearchIcon sx={{ fontSize: 14, color: TOKENS.ink400 }} />
            <Box
              component="input"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              placeholder="Search…"
              sx={{
                border: 'none',
                outline: 'none',
                fontSize: 11.5,
                width: '100%',
                ...T,
                background: 'transparent',
              }}
            />
          </Box>
          <Box
            component="button"
            onClick={onClear}
            sx={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              p: '2px',
              color: TOKENS.ink400,
              '&:hover': { color: TOKENS.ink900 },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, p: '12px' }}>
          {tab === 'overview' && (
            <>
              <Box
                sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 1.5 }}
              >
                <DowntimeCard downtimeStr={downtimeStr} />
                {/* Schedule summary card */}
                <Box
                  sx={{
                    flex: '1 1 230px',
                    minWidth: 220,
                    border: `1px solid ${TOKENS.line200}`,
                    borderTop: `2px solid ${hasProd ? TOKENS.brandTeal : TOKENS.line300}`,
                    borderRadius: '2px',
                    background: '#fff',
                    p: '9px 11px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: 0.875,
                    }}
                  >
                    <CalendarMonthIcon
                      sx={{
                        fontSize: 14,
                        color: hasProd ? TOKENS.brandTeal : TOKENS.ink400,
                      }}
                    />
                    <Box
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: TOKENS.ink400,
                        flex: 1,
                      }}
                    >
                      Schedule
                    </Box>
                    {hasProd && (
                      <Box
                        component="span"
                        onClick={() => setTab('schedule')}
                        sx={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: TOKENS.brandTeal,
                          cursor: 'pointer',
                        }}
                      >
                        View ↗
                      </Box>
                    )}
                  </Box>
                  {!hasRun ? (
                    <Box sx={{ fontSize: 11, color: TOKENS.ink400 }}>
                      Run the scheduler to generate this node's schedule.
                    </Box>
                  ) : !hasProd ? (
                    <Box sx={{ fontSize: 11, color: TOKENS.ink400 }}>
                      No production scheduled on this node.
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box>
                        <Box
                          sx={{
                            ...MONO_SX,
                            fontSize: 17,
                            fontWeight: 700,
                            color: TOKENS.ink900,
                          }}
                        >
                          {resourceSchedule.length}
                        </Box>
                        <Box sx={{ fontSize: 9.5, color: TOKENS.ink400 }}>
                          Batches
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <SourceTablesStrip onOpenTable={onOpenTable} />
            </>
          )}

          {tab === 'input' && (
            <>
              <SourceTablesStrip onOpenTable={onOpenTable} />
              {type === 'kitchen' && (
                <FDAccordion
                  icon={<InventoryIcon />}
                  title="Base materials & products"
                  count={products.length}
                  defaultOpen
                  onOpenTable={() => onOpenTable?.('Packaging Routes')}
                >
                  {products
                    .filter(
                      (p) =>
                        !search ||
                        p.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((code) => (
                      <Box
                        key={code}
                        sx={{
                          border: `1px solid ${TOKENS.line200}`,
                          borderRadius: '2px',
                          p: '7px 9px',
                          mb: 0.75,
                          background: TOKENS.surface50,
                        }}
                      >
                        <Box
                          sx={{
                            ...MONO_SX,
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: TOKENS.blue600,
                          }}
                        >
                          {code}
                        </Box>
                      </Box>
                    ))}
                </FDAccordion>
              )}
              {type === 'line' && (
                <>
                  <FDAccordion
                    icon={<InventoryIcon />}
                    title="Products"
                    count={products.length}
                    defaultOpen
                    onOpenTable={() => onOpenTable?.('Packaging Routes')}
                  >
                    {products
                      .filter(
                        (p) =>
                          !search ||
                          p.toLowerCase().includes(search.toLowerCase())
                      )
                      .map((code) => {
                        const route = packagingRoutes.find(
                          (r) => r.productCode === code
                        )
                        return (
                          <Box
                            key={code}
                            sx={{
                              border: `1px solid ${TOKENS.line200}`,
                              borderRadius: '2px',
                              p: '7px 9px',
                              mb: 0.75,
                              background: TOKENS.surface50,
                            }}
                          >
                            <Row>
                              <Box
                                sx={{
                                  ...MONO_SX,
                                  fontSize: 11.5,
                                  fontWeight: 600,
                                  color: TOKENS.blue600,
                                }}
                              >
                                {code}
                              </Box>
                              {route?.kitchenRunRate && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 9.5,
                                    color: TOKENS.ink400,
                                    ml: 0.5,
                                  }}
                                >
                                  {route.kitchenRunRate} u/hr
                                </Box>
                              )}
                            </Row>
                          </Box>
                        )
                      })}
                  </FDAccordion>
                  {cleaningPrograms.length > 0 && (
                    <FDAccordion
                      icon={<CleaningServicesIcon />}
                      title="Cleaning programs"
                      count={cleaningPrograms.length}
                      onOpenTable={() => onOpenTable?.('Cleaning Mapping')}
                    >
                      {cleaningPrograms.map((p, i) => (
                        <SubPaper key={i}>
                          <Box
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: TOKENS.ink700,
                            }}
                          >
                            {p.programName || '—'}
                          </Box>
                          <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>
                            CIP: {p.cip}
                          </Box>
                          <InfoRow
                            label="Total time"
                            value={p.totalTargetTime || '—'}
                          />
                        </SubPaper>
                      ))}
                    </FDAccordion>
                  )}
                  {syncGroups.length > 0 && (
                    <FDAccordion
                      icon={<SyncIcon />}
                      title="Synchronized groups"
                      count={syncGroups.length}
                      onOpenTable={() => onOpenTable?.('Synchronized Lines')}
                    >
                      {syncGroups.map((sg) => (
                        <SubPaper key={sg.id}>
                          <Box sx={{ fontSize: 12, fontWeight: 600, mb: 0.75 }}>
                            {sg.groupName}
                          </Box>
                          <InfoRow label="Type" value={sg.productMasterType} />
                          <InfoRow
                            label="Changeover time"
                            value={`${sg.changeoverTime}h`}
                          />
                        </SubPaper>
                      ))}
                    </FDAccordion>
                  )}
                </>
              )}
              {type === 'cip' && cleaningPrograms.length > 0 && (
                <FDAccordion
                  icon={<CleaningServicesIcon />}
                  title="Cleaning programs"
                  count={cleaningPrograms.length}
                  defaultOpen
                >
                  {cleaningPrograms.map((p, i) => (
                    <SubPaper key={i}>
                      <Box sx={{ fontSize: 12, fontWeight: 600 }}>
                        {p.programName || '—'}
                      </Box>
                      <InfoRow
                        label="Total time"
                        value={p.totalTargetTime || '—'}
                      />
                      <InfoRow label="Caustic" value={p.causticTime || '—'} />
                      <InfoRow
                        label="Final rinse"
                        value={p.finalRinseTime || '—'}
                      />
                      <InfoRow label="Drain" value={p.drainTime || '—'} />
                    </SubPaper>
                  ))}
                </FDAccordion>
              )}
            </>
          )}

          {tab === 'schedule' &&
            type !== 'cip' &&
            (hasProd ? (
              <ResourceGantt
                rows={resourceSchedule}
                mode={type as 'kitchen' | 'line'}
                resourceName={name}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: TOKENS.ink400,
                  textAlign: 'center',
                  gap: '6px',
                }}
              >
                <CalendarMonthIcon
                  sx={{ fontSize: 28, color: TOKENS.line300 }}
                />
                <Box
                  sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.ink500 }}
                >
                  {hasRun
                    ? 'No production scheduled on this node'
                    : 'No schedule yet'}
                </Box>
                <Box sx={{ fontSize: 11.5 }}>
                  {hasRun
                    ? 'This resource has no production batches this week.'
                    : "Run the scheduler to generate this node's schedule."}
                </Box>
              </Box>
            ))}
        </Box>
      </Box>
    )
  }
)
FactoryBottomDetail.displayName = 'FactoryBottomDetail'
